import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, runTransaction, doc, query, where, getDocs, getDoc, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import { useAuth } from '../../../hooks/useAuth';
import { auth } from '../../../firebase';
import { calculateFinalPrice, findDiscountByMemberType } from '../../../utils/pricing';
import ClientLayout from '../../../components/layout/ClientLayout';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import SmsService from '../../../services/SmsService';
import {
  FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS,
  getFacilityReservationApplicationWindowMessage,
  isWithinFacilityReservationApplicationWindow
} from '../../../utils/facilityReservationDate';

interface ReservationData {
  facilityId: string;
  facilityName: string;
  reservationDate: Date;
  startTime: number;
  endTime: number;
  reservationType: 'time-slot' | 'start-end';
  uid: string;
  name: string;
  email: string;
  phoneNumber: string;
  organization: string;
}

interface Facility {
  name: string;
  price: number;
  basePrice?: number;
  maxPrice?: number;
  reservationDays?: number;
  reservationWindowDays?: number;
  reservationTimeUnit: number;
  discounts?: Array<{ memberType: string | number; discountRate: number }>;
}

interface ReservationFormData {
  uid: string;
  name: string;
  email: string;
  phoneNumber: string;
  organization: string;
  facilityId: string;
  facilityName: string;
  reservationDate: Date;
  startTime: number;
  endTime: number;
  reservationType: string;
  status: string;
  attachmentUrl: string | null;
  createdAt: Date;
  updatedAt: Date;
  statusHistory: Array<{
    status: string;
    changedAt: Date;
    changedBy: string;
    reason: string;
  }>;
}

const FACILITY_APPLICATION_FORM_TEMPLATE_URL =
  'https://futuremnc.mycafe24.com/bnfc/downloads/download.php?file=시설_사용신청서.pdf';

const JOKGU_APPLICATION_FORM_TEMPLATE_URL =
  'https://futuremnc.mycafe24.com/bnfc/downloads/download.php?file=족구장_사용신청서.pdf';

const MAX_RESERVATION_UPLOAD_SIZE_MB = 5;
const MAX_RESERVATION_UPLOAD_SIZE_BYTES = MAX_RESERVATION_UPLOAD_SIZE_MB * 1024 * 1024;
const RESERVATION_UPLOAD_TIMEOUT_MS = 90 * 1000;
const CONTRACTOR_USER_LEVEL = 3;

const getEffectiveReservationDays = (facility: Facility, userLevel: number | null) => {
  if (userLevel === CONTRACTOR_USER_LEVEL) return 0;
  return facility.reservationDays ?? 0;
};

const RENTAL_TERMS_FULL_TEXT = `제1조【목적】이 규약은 복지플러스센터 늘해랑쉼터 내 안전교육장과 부산항 홍보관 대/소 회의실의 대관절차 및 사용료 등을 규정함으로써 시설의 효율적인 운영과 항만근로자 복지증진에 기여코자함을 목적으로 한다.

제2조【대관시간】시설의 대관시간은 다음 각 호와 같이 구분하며, 1시간 미만을 사용하는 때에도 1시간을 사용하는 것으로 본다.
1. 기본시간 : 1시간
2. 추가시간 : 1시간 단위로 추가 사용할 수 있으며, 1시간 미만을 추가 사용하는 경우에도 1시간을 사용한 것으로 본다.
3. 종 일 : 09 ~ 18시

제3조【대관신청】① 시설을 사용하고자 하는 자(이하 “신청인”라 한다)는 [별지 제1호 서식]에 따른 시설사용신청서(이하 “신청서”라 한다)를 아스타아이비에스(이하 “관리주체”)에 제출하여 승인을 받아야 한다.
② 신청인이 제1항에 따른 대관신청 시 대관개시일로부터 10일 이전에 신청함을 원칙으로 한다.

제4조【대관승인】① 관리주체는 신청서를 접수한 날로부터 7일 이내에 사용신청에 대한 승인 여부를 신청인에게 통지하여야 한다.
② 동 시간대 대관신청이 있는 경우 신청인이 요청한 대관기간에 상관없이 접수 순위가 빠른 신청인을 우선한다.
③ 공사 혹은 관리주체는 대관승인과 관련하여 시설물 및 이용객 안전관리 등을 위해 필요한 경우 별도조건을 부과할 수 있으며, 신청인과 협의하여 부속설비의 범위와 대관기간 및 대관시간을 조정할 수 있다.
④ 대관에 대한 내용이 많거나 책임한계를 분명히 할 필요가 있을 경우 공사와 신청인 간 계약서를 작성 할 수 있으며, 계약 내용은 공사 내부방침에 따른다.
⑤ 제4항의 경우 공사는 계약체결 즉시 결과를 관리주체에 서면으로 통보한다.
⑥ 관리주체는 대관 승인 이후에도 필요하다고 인정될 경우 시설물 사용과 관련된 자료 제출을 요구할 수 있으며 신청인은 관리주체와의 협의를 통해 제출 시기 및 범위를 조정할 수 있다.

제5조【대관승인의 제한】다음 각 호의 하나에 해당하는 경우 공사 혹은 관리
주체는 대관승인을 제한 할 수 있다.
1. 공공질서 유지 및 미풍양속을 해할 우려가 있다고 판단되는 경우
2. 법규를 위반하는 내용 혹은 행위를 할 우려가 명백한 공연 및 행사
3. 대관시설의 효율적인 관리ㆍ운영에 지장이 있다고 판단되는 경우
4. 시설 및 설비를 훼손할 우려가 있거나 안전관리에 부적합하다고 인정될 경우
5. 특정 종교의 포교, 정치적인 목적 또는 노동집회 등의 경우
6. 다단계 등 각종 사업설명회 목적으로 사용하는 경우
7. 기타 대관승인 사항의 불이행이나 취소 등 본 규정을 위반하였을 경우,
위반일 기준으로 2년 간 대관승인을 제한한다.
8. 대관료 미납 또는 체납된 단체

제6조【대관승인의 변경】① 공사 혹은 관리주체는 공사의 업무 등 부득이한 사유가 발생한 경우 신청인의 동의를 얻어 대관기간 혹은 내용을 변경할 수 있다.
② 제4조제1항에 따라 대관승인 통보를 받은 자는 1회에 한하여 대관개시일 7일 전까지 공사 혹은 관리주체의 승인을 얻어 신청내용의 일부를 변경할 수
있다. 다만, 다음 각 호에 어느 하나에 해당하는 사항은 변경할 수 없다.
1. 제4조제3항에 따른 별도 조건
2. 신청인
③ 제2항에도 불구하고 신청인이 천재지변 등 기타 불가항력의 사유로 인하여 시설 사용이 불가능한 경우 공사 혹은 관리주체와 상호 협의하여 대관기간 및 시간을 변경할 수 있으며, 이 경우 제2항에 따른 변경횟수에 기산하지 아니한다.

제7조【대관승인의 취소 등】① 공사 혹은 관리주체는 다음 각 호의 어느 하나에 해당하는 경우 대관승인을 취소 및 변경, 그 사용을 제한 및 정지시킬 수 있다.
1. 대관승인 후 신청서 기재 사실이 허위로 밝혀졌을 경우
2. 본 규약에서 정한 기일 내 대관료를 납부하지 아니한 경우
3. 공사 혹은 관리주체 사전승인 없이 시설능력을 초과하여 운영한 경우
4. 대관시설의 관리ㆍ운영 질서를 현저히 문란하게 한 경우
5. 신청인이 정당한 이유 없이 이 규약에 따른 공사 혹은 관리주체의 요구에 불응한 경우
6. 신청인이 사용목적을 위반하여 제5조 각 호의 목적으로 사용하는 경우
7. 신청인이 제6조제2항제2호에도 불구하고 대관에 대한 권리를 타인에게 양도 혹은 전대한 경우

제8조【대관신청의 자격정지】공사는 대관승인 후 아래 각 호 어느 하나에 해당하는 행위를 한 자에 대하여 그 행위 사실을 알게 된 때로부터 2년 간 대관신청 자격을 정지시킬 수 있다.
1. 신청인이 대관신청 후 연 2회 이상 대관취소를 한 경우
2. 신청인의 미수금이 발생한 경우
3. 본 규정에서 정하는 신청인의 의무를 다하지 아니한 경우
4. 제7조에 따라 대관이 취소 혹은 사용이 제한 또는 정지된 경우

제9조【대관료】① 대관시설에 대한 사용료는 [별표 1]과 같다.
② 사용료는 선납을 원칙으로 하며, 대관개시일 3일 이전까지 관리주체가 지정한 계좌로 납부해야한다
③ 신청인이 대관시설을 2일 이상 연속하여 사용 희망하는 경우에도 대관료는 [별표 1] 기준에 따라 산정 및 부과한다.

제10조【대관료의 반환】① 제9조제2항에 따라 대관료를 선납하였으나, 대관사유 허위기재 등 신청인의 귀책사유로 대관승인이 취소된 경우 선납 대관료는 반환하지 아니한다.
② 대관료는 다음 각 호의 어느 하나에 해당하는 경우를 제외하고는 반환하지 아니한다.
1. 천재지변 혹은 기타 불가항력적인 사유로 대관이 불가능 할 경우 전액반환
2. 공사 혹은 관리주체의 귀책사유, 사정으로 인해 대관이 취소ㆍ정지될 경우 전액반환
3. 신청인이 대관개시일 7일 전에 대관신청을 취소하는 경우 전액반환
4. 신청인이 대관개시일 3일 전에 대관신청을 취소하는 경우 50% 반환
③ 제2항 각 호에 의한 대관료 반환은 기 사용기간을 제외한 나머지 기간에 대하여 반환함을 원칙으로 한다.

제11조【대관료 특례】① 공사는 다음 각 호의 어느 하나에 해당하는 경우 신청인의 대관료를 면제 또는 감면할 수 있으며, 본 규약에서 정하지 않는 사항에 대하여는 공사 내부방침에 따라 따로 정할 수 있다.
1. 국가, 지자체 혹은 공공기관이 사용하는 경우에는 사용요금의 100분의 30을 감면할 수 있다.
2. 항만근로자, 사회적 약자 사회공헌 행사시 사용요금의 100분의 50을 감면할 수 있다.

제12조【신청인의 의무】① 신청인은 대관기간 중 시설, 장비, 부대시설에 대하여 선량한 관리자로서 주의의무를 다하여야 한다.
② 신청인은 관리의무를 태만히 하여 대관시설, 설비, 장비 등을 훼손 혹은 멸실 시 해당 시설을 원상복구 해야 하며, 손해가 발생한 경우 이를 배상해야 한다.
③ 신청인은 시설 사용과 관련하여 본 규약 및 공사 혹은 관리주체의 요구사항을 성실히 이행해야 한다.

제13조【신청인의 안전관리】① 신청인은 대관시설 사용 시 관리주체와 협의하여 안전관리계획을 수립해야한다.
② 신청인은 대관시설에 별도의 설비 또는 장비 등을 설치하는 경우, 별도 설치되는 시설물은 필히 방염처리를 하여 화재예방에 최선을 다해야 한다.
③ 관리업체와 협의가 되어 반입된 추가 장비 및 설비에 대한 관리 책임은 신청인에게 있다.
④ 신청인은 대관시설 사용 시 화재발생의 우려가 있는 폭죽, 총기, 화약류, 인화성물질 등을 사용해서는 안 된다.
⑤ 신청인이 대관시설이 아닌 외부의 조명, 음향 등을 사용할 경우 관리주체 전기안전관리자 입회하에 실시한 안전점검을 시행 후 전기를 사용할 수 있다.
⑥ 상기 각 항의 규정을 위반한 자에 대하여 공사 혹은 관리주체는 대관시설의 사용을 제한할 수 있으며, 이로 인한 손해배상 책임을 지지 아니한다.

제14조【신청인의 설비】① 신청인이 대관기간 중 특별한 장치와 설비(이하 “시설물”이라 한다.)를 하고자 할 때는 사전이 공사 혹은 관리주체의 승인을 받아 신청인 부담으로 설치 할 수 있다.
② 공사 혹은 관리주체는 필요할 경우 신청인에 예치금을 부담하게 할 수 있으며, 예치금은 시설물 설치 규모 및 정도에 따라 따로 정하여 산정ㆍ부과한다.
③ 시설물을 설치했을 때 대관기간 만료와 동시에 이를 철거하고 원상복구 해야 한다.
④ 신청인이 제3항에서 정한 의무를 이행하지 아니하는 때에는 관리주체가 직접 원상복구하고 그 비용을 신청인에게 징수할 수 있다.
⑤ 제4항에 따른 원상복구 중 발생한 시설물 파손에 대하여 공사는 파손에 대한 책임을 지지 않는다.

제15조【입장권 발행】① 신청인은 대관시설의 질서 유지와 유료 입장 등이 필요한 경우 입장권을 발행할 수 있으며, 발행ㆍ관리에 대한 세부사항은 공사 혹은 관리주체와 신청인이 협의하여 정한다.

제16조【입장제한】공사 혹은 관리주체는 다음 각 호의 어느 하나에 해당하는 경우 시설의 출입을 제한할 수 있다.
1. 시설 이용에 정당한 권한이 없는 경우
2. 만취 등 타인이 혐오할 만한 결함이 있거나 전염병 질환이 있는 경우
3. 시설 이용에 현저한 방해를 주거나 방해가 될 염려가 있는 경우
4. 시설 이용자가 위험물질을 소지한 경우 또는 이에 준하는 위험요인이 있는 경우
5. 공공질서를 해칠 우려가 있거나 이용 목적에 부적합한 경우
6. 신청인은 대관기간 중 시설의 입장제한에 해당하는 경우에 대한 예방조치를 해야 한다.

제17조【배상책임】① 공사 혹은 관리주체는 본 규약에 의하여 대관승인을 취소, 변경, 제한, 정지 시켰을 때 발생하는 손해 및 손실보상의 책임을 지지 아니하며, 신청인은 이의를 제기할 수 없다.
② 신청인은 대관시설, 장비, 도구의 파손 혹은 멸실로 인한 재산상 손해를 배상해야 한다.
③ 신청인은 신청인 귀책사유로 인하여 발생한 시설 이용객의 신체적, 정신적, 물질적 피해에 대하여 이를 배상해야 한다.
④ 기타 신청인의 귀책사유 등으로 인한 불법행위 책임은 관계법규 등에 따른다.

제18조【적용규약】① 이 규약은 공사 혹은 관리주체와 신청인 간 쌍방 계약의 일부로 간주되며, 계약서와 동일한 효력을 지니며 관계법령 및 이 규정에 명시되지 않은 사항은 공사가 정한 바에 따른다.
④ 기타 신청인의 귀책사유 등으로 인한 불법행위 책임은 관계법규 등에 따른다.

제19조【규정되지 않은 사항에 대한 결정】복지플러스센터 늘해랑쉼터 내 안전교육장과 부산항 홍보관 회의실 대관에 관하여 이 규약에 명시되지 아니한 사항에 대하여는 공사에서 정하는 바에 따른다.

부 칙
제1조【시행일】 이 규약은 승인을 받은 날로부터 시행한다.
부 칙(2024. 3. 1.)
제1조【시행일】 이 규약은 2024년 3월 1일부터 시행한다.
부 칙(2024. 12. 1.)
제1조【시행일】 이 규약은 2024년 12월 1일부터 시행한다.
부 칙(2025. 6. 5.)
제1조【시행일】 이 규약은 2025년 6월 5일부터 시행한다.`;

const FacilityReservationConfirmPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [applicationFormFile, setApplicationFormFile] = useState<File | null>(null);
  const [submitStatus, setSubmitStatus] = useState<string | null>(null);
  const [hasAgreedRentalTerms, setHasAgreedRentalTerms] = useState(false);
  const [isRentalTermsModalOpen, setIsRentalTermsModalOpen] = useState(false);
  const [facility, setFacility] = useState<Facility | null>(null);
  const [userProfile, setUserProfile] = useState<{ userLevel: number } | null>(null);
  const [selectedDiscountType, setSelectedDiscountType] = useState<string>('');
  const [appliedUserLevel, setAppliedUserLevel] = useState<number | null>(null);
  const { loading: emailVerificationLoading } = useEmailVerification();

  const locationState = location.state?.reservationData as ReservationData;
  const isJokguFacility = (facility?.name ?? locationState?.facilityName ?? '').includes('족구장');
  const applicationFormTemplateUrl = isJokguFacility
    ? JOKGU_APPLICATION_FORM_TEMPLATE_URL
    : FACILITY_APPLICATION_FORM_TEMPLATE_URL;
  const applicationFormTemplateFileName = isJokguFacility ? '대관신청서_양식_족구장.pdf' : '대관신청서_양식.pdf';

  // 관리자에게 SMS 발송하는 함수
  const sendNotificationToAdmins = async (facilityName: string, reservationDate: Date) => {
    try {
      // 당일 예약인지 확인
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const reservationDay = new Date(reservationDate);
      reservationDay.setHours(0, 0, 0, 0);
      
      // 당일 예약이 아니면 SMS 발송하지 않음
      if (reservationDay.getTime() !== today.getTime()) {
        return;
      }

      // SMS 알림을 받을 관리자들 조회
      const adminQuery = query(
        collection(db, 'user_profiles'),
        where('userLevel', 'in', [1, 2]), // 최고관리자(1) 또는 일반관리자(2)
        where('smsNotifications.facilityReservations', '==', true)
      );
      
      const adminSnapshot = await getDocs(adminQuery);
      const notificationAdmins = adminSnapshot.docs
        .map(doc => ({
          uid: doc.id,
          ...doc.data()
        }))
        .filter((admin: any) => admin.phoneNumber); // 전화번호가 있는 관리자만

      if (notificationAdmins.length === 0) {
        console.log('SMS 알림을 받을 관리자가 없습니다.');
        return;
      }

      // 각 관리자에게 SMS 발송
      const smsMessage = `[BNFC] 시설물 당일 예약 신청이 접수되었습니다. 홈페이지에서 확인해주세요.`;
      
      const promises = notificationAdmins.map(async (admin: any) => {
        try {
          await SmsService.sendSMS(admin.phoneNumber, smsMessage);
          console.log(`관리자 SMS 발송 완료: ${admin.phoneNumber}`);
        } catch (error) {
          console.error(`관리자 SMS 발송 실패 (${admin.phoneNumber}):`, error);
        }
      });

      await Promise.allSettled(promises);
      console.log(`시설물 예약 안내 SMS 발송 완료 (대상: ${notificationAdmins.length}명)`);
    } catch (error) {
      console.error('관리자 SMS 발송 중 오류:', error);
    }
  };

  useEffect(() => {
    if (!locationState) {
      navigate('/facility');
      return;
    }

    // 시설물 정보 가져오기
    const fetchFacility = async () => {
      try {
        const facilityDoc = await getDoc(doc(db, 'facilities', locationState.facilityId));
        if (facilityDoc.exists()) {
          setFacility(facilityDoc.data() as Facility);
        }
      } catch (err) {
        console.error('시설물 정보 조회 실패:', err);
      }
    };

    // 사용자 프로필 정보 가져오기
    const fetchUserProfile = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          navigate('/signin');
          return;
        }

        const userDoc = await getDoc(doc(db, 'user_profiles', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data() as { userLevel: number };
          setUserProfile(userData);
          setAppliedUserLevel(userData.userLevel);
        }
      } catch (err) {
        console.error('사용자 정보 조회 실패:', err);
      }
    };

    fetchFacility();
    fetchUserProfile();
  }, [locationState, navigate]);

  const validateReservationFileSize = (selectedFile: File, label: string) => {
    if (selectedFile.size <= MAX_RESERVATION_UPLOAD_SIZE_BYTES) {
      return true;
    }

    setError(`${label}는 ${MAX_RESERVATION_UPLOAD_SIZE_MB}MB 이하 파일만 첨부할 수 있습니다.`);
    return false;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!validateReservationFileSize(selectedFile, '추가 서류')) {
        setFile(null);
        setFileUrl(null);
        e.target.value = '';
        return;
      }

      setFile(selectedFile);
      setFileUrl(null);
      setError(prev => (prev?.includes('MB 이하 파일') ? null : prev));
    }
  };

  const handleApplicationFormFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!validateReservationFileSize(selectedFile, '대관신청서')) {
        setApplicationFormFile(null);
        e.target.value = '';
        return;
      }

      setApplicationFormFile(selectedFile);
      setError(prev => (prev?.includes('MB 이하 파일') ? null : prev));
    }
  };

  const handleDiscountChange = (discountType: string) => {
    setSelectedDiscountType(discountType);
    // appliedUserLevel은 원래 사용자 레벨 유지 (가격 계산은 별도 함수에서 처리)
    setAppliedUserLevel(userProfile?.userLevel || 9);
  };

  const withTimeout = async <T,>(promise: Promise<T>, timeoutMessage: string): Promise<T> => {
    let timeoutId: number | undefined;

    try {
      return await Promise.race([
        promise,
        new Promise<T>((_, reject) => {
          timeoutId = window.setTimeout(() => reject(new Error(timeoutMessage)), RESERVATION_UPLOAD_TIMEOUT_MS);
        })
      ]);
    } finally {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    }
  };

  const uploadReservationFile = async (
    selectedFile: File,
    storagePath: string,
    label: string
  ): Promise<string> => {
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, selectedFile, {
      contentType: selectedFile.type || 'application/octet-stream'
    });

    let unsubscribe: (() => void) | null = null;

    try {
      await new Promise<void>((resolve, reject) => {
        let settled = false;
        const timeoutId = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          if (unsubscribe) unsubscribe();
          uploadTask.cancel();
          reject(new Error(`${label} 업로드 시간이 초과되었습니다. 네트워크 상태를 확인한 뒤 다시 시도해주세요.`));
        }, RESERVATION_UPLOAD_TIMEOUT_MS);

        unsubscribe = uploadTask.on(
          'state_changed',
          snapshot => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setSubmitStatus(`${label} 업로드 중... ${progress}%`);
          },
          error => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            reject(error);
          },
          () => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            resolve();
          }
        );
      });

      setSubmitStatus(`${label} 업로드 완료. 파일 주소 확인 중...`);
      return await withTimeout(
        getDownloadURL(storageRef),
        `${label} 파일 주소 확인 시간이 초과되었습니다. 네트워크 상태를 확인한 뒤 다시 시도해주세요.`
      );
    } catch (error) {
      if (error instanceof Error && error.message.includes('시간이 초과')) {
        throw error;
      }

      throw new Error(`${label} 업로드 중 오류가 발생했습니다. 파일 형식과 네트워크 상태를 확인해주세요.`);
    } finally {
      const currentUnsubscribe = unsubscribe as unknown as (() => void) | null;
      if (currentUnsubscribe) currentUnsubscribe();
    }
  };

  const VAT_RATE = 0.1;

  const calculateEstimatedPriceBreakdown = () => {
    if (!facility) return { net: 0, vat: 0, total: 0 };
    let netPrice = 0;

    // 일반사용자(level 9)가 감면 사유를 선택한 경우
    if (userProfile?.userLevel === 9 && selectedDiscountType && facility.discounts) {
      // 새로운 pricing utils 사용
      const facilityPriceInfo = {
        price: facility.price,
        basePrice: facility.basePrice,
        maxPrice: facility.maxPrice,
        discounts: facility.discounts
      };
      
      const reservationInfo = {
        reservationType: locationState.reservationType,
        startTime: locationState.startTime,
        endTime: locationState.endTime,
        reservationTimeUnit: facility.reservationTimeUnit
      };

      // 기본 가격 계산 (새로운 로직 포함)
      const basePrice = calculateFinalPrice(facilityPriceInfo, reservationInfo, 9);
      
      // 선택된 감면 사유에 따른 할인율 적용
      const discount = findDiscountByMemberType(facility.discounts, selectedDiscountType);
      
      if (discount) {
        netPrice = Math.floor(basePrice * (1 - discount.discountRate / 100));
      } else {
        netPrice = basePrice;
      }
    } else {
      // 등업된 사용자이거나 일반사용자가 감면 사유를 선택하지 않은 경우
      // 새로운 pricing utils 사용
      netPrice = calculateFinalPrice(
        {
          price: facility.price,
          basePrice: facility.basePrice,
          maxPrice: facility.maxPrice,
          discounts: facility.discounts
        },
        {
          reservationType: locationState.reservationType,
          startTime: locationState.startTime,
          endTime: locationState.endTime,
          reservationTimeUnit: facility.reservationTimeUnit
        },
        appliedUserLevel || 9
      );
    }

    const vat = Math.round(netPrice * VAT_RATE);
    return { net: netPrice, vat, total: netPrice + vat };
  };

  const getDiscountRate = () => {
    if (!facility?.discounts || !selectedDiscountType) return 0;
    
    const discount = findDiscountByMemberType(facility.discounts, selectedDiscountType);
    return discount ? discount.discountRate : 0;
  };

  // 회원 등급 레벨을 한글 명칭으로 변환
  const getUserLevelText = (level: string | number): string => {
    const levelNum = parseInt(String(level), 10);
    switch (levelNum) {
      case 1: return '최고 관리자';
      case 2: return '일반 관리자';
      case 3: return '공사 관계자';
      case 4: return '센터 관계자';
      case 5: return '입주업체';
      case 6: return '입주협력업체';
      case 7: return '공공기관';
      case 8: return '항만근로자';
      case 9: return '일반 사용자';
      default: return `레벨 ${level}`;
    }
  };

  const handleSubmit = async () => {
    if (!user || !facility) return;

    setLoading(true);
    setError(null);
    setSubmitStatus('예약 신청을 준비 중입니다...');

    try {
      // 사용자 레벨 재확인 (필수 검증을 위해 최신 값 사용)
      let profile = userProfile;
      if (!profile) {
        const userDoc = await getDoc(doc(db, 'user_profiles', user.uid));
        if (userDoc.exists()) {
          profile = userDoc.data() as { userLevel: number };
          setUserProfile(profile);
          setAppliedUserLevel(profile.userLevel);
        }
      }

      const profileUserLevel = profile?.userLevel ?? 9;
      // userLevel 1,2,3은 면제 / 4~9는 대관규약+대관신청서 필수
      const requiresPreConfirmChecks = profileUserLevel >= 4;
      const effectiveAppliedUserLevel = appliedUserLevel ?? profileUserLevel ?? 9;
      const effectiveReservationDays = getEffectiveReservationDays(facility, profileUserLevel);

      const reservationWindowDays = facility.reservationWindowDays ?? FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS;
      if (!isWithinFacilityReservationApplicationWindow(
        locationState.reservationDate,
        new Date(),
        effectiveReservationDays,
        reservationWindowDays
      )) {
        setError(getFacilityReservationApplicationWindowMessage(reservationWindowDays));
        return;
      }

      if (requiresPreConfirmChecks && !hasAgreedRentalTerms) {
        setError('예약 신청을 위해 대관규약에 동의해주세요.');
        return;
      }

      if (requiresPreConfirmChecks && !applicationFormFile) {
        setError('예약 신청을 위해 대관신청서를 첨부해주세요.');
        return;
      }

      let fileUrl = null;
      if (file) {
        fileUrl = await uploadReservationFile(
          file,
          `reservations/${user.uid}/${Date.now()}_${file.name}`,
          '추가 서류'
        );
      }

      let applicationFormUrl = null;
      let applicationFormFileName = null;
      if (requiresPreConfirmChecks && applicationFormFile) {
        applicationFormUrl = await uploadReservationFile(
          applicationFormFile,
          `reservations/${user.uid}/application-forms/${Date.now()}_${applicationFormFile.name}`,
          '대관신청서'
        );
        applicationFormFileName = applicationFormFile.name;
      }

      setSubmitStatus('예약 정보를 저장 중입니다...');
      const reservationData = {
        uid: user.uid,
        facilityId: locationState.facilityId,
        facilityName: facility.name,
        reservationDate: Timestamp.fromDate(locationState.reservationDate),
        startTime: locationState.startTime,
        endTime: locationState.reservationType === 'time-slot' 
          ? locationState.startTime + facility.reservationTimeUnit 
          : locationState.endTime,
        reservationType: locationState.reservationType,
        name: locationState.name,
        email: locationState.email,
        phoneNumber: locationState.phoneNumber || '',
        organization: locationState.organization || '',
        fileUrl,
        status: 'pending',
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
        price: calculateEstimatedPriceBreakdown().total, // 부가세 포함 최종 가격 저장
        userLevel: effectiveAppliedUserLevel, // 적용된 userLevel 저장
        discountType: selectedDiscountType || null, // 선택된 감면 사유 저장
        ...(requiresPreConfirmChecks
          ? {
              rentalTermsAgreed: true,
              rentalTermsAgreedAt: Timestamp.fromDate(new Date()),
              applicationFormUrl,
              applicationFormFileName
            }
          : {})
      };

      await addDoc(collection(db, 'reservations'), reservationData);
      
      // 관리자에게 SMS 알림 발송 (당일 예약인 경우만)
      setSubmitStatus('예약 신청을 마무리하는 중입니다...');
      await sendNotificationToAdmins(facility.name, locationState.reservationDate);
      
      navigate('/facility/reservations');
    } catch (err) {
      console.error('예약 신청 실패:', err);
      setError(err instanceof Error ? err.message : '예약 신청 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
      setSubmitStatus(null);
    }
  };

  if (!locationState || !facility || emailVerificationLoading) {
    return (
      <ClientLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <p className="text-gray-500">예약 정보를 찾을 수 없습니다.</p>
        </div>
      </ClientLayout>
    );
  }

  const estimatedPrice = calculateEstimatedPriceBreakdown();

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-white">
        {/* 본문 컨테이너 - 중앙 정렬 */}
        <div className="w-full max-w-[760px] mx-auto relative pt-8 px-4 sm:px-6 lg:px-8">
          {/* 헤더 섹션 */}
          <div className="mt-8">
            <div className="text-slate-800 text-2xl sm:text-3xl lg:text-4xl font-extrabold font-['Pretendard'] leading-tight">
              예약 확인
            </div>
            <div className="mt-3 sm:mt-4 text-slate-700 text-base sm:text-lg font-normal font-['Pretendard'] leading-relaxed">
              예약 정보를 확인하고 예약을 진행해주세요
            </div>
          </div>

          {/* 구분선 */}
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

          {error && (
            <div className="mt-8 bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            {/* 예약 정보 섹션 */}
            <div className="p-4 sm:p-6 lg:p-8">
              <h2 className="text-[#1e2c49] text-lg sm:text-xl font-bold font-['Pretendard'] leading-tight mb-4">
                시설물 정보
              </h2>
              <div className="w-full h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6 sm:mb-8"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">시설물</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">{locationState.facilityName}</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">예약 날짜</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">
                    {locationState.reservationDate.toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      weekday: 'long'
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">시작 시간</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">{locationState.startTime}:00</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">종료 시간</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">
                    {locationState.reservationType === 'time-slot' 
                      ? `${locationState.startTime + facility.reservationTimeUnit}:00`
                      : `${locationState.endTime}:00`}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">예약 유형</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">
                    {locationState.reservationType === 'time-slot' ? '시간대형' : '시작종료형'}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">예상 가격</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold text-blue-600">
                    {estimatedPrice.total.toLocaleString()}원
                  </p>
                  <p className="text-sm text-[#6b7684] mt-1">
                    (부가세 10% {estimatedPrice.vat.toLocaleString()}원 포함)
                  </p>
                  {/* 일반사용자가 감면 사유를 선택한 경우 */}
                  {userProfile?.userLevel === 9 && selectedDiscountType && getDiscountRate() > 0 && (
                    <p className="text-sm text-green-600 mt-1">
                      {getUserLevelText(selectedDiscountType)} 할인 {getDiscountRate()}% 적용됨
                    </p>
                  )}
                  {/* 등업된 사용자의 자동 할인 표시 */}
                  {userProfile && userProfile.userLevel !== 9 && facility?.discounts && (() => {
                    const userDiscount = findDiscountByMemberType(facility.discounts, userProfile.userLevel);
                    return userDiscount ? (
                      <p className="text-sm text-blue-600 mt-1">
                        {getUserLevelText(userProfile.userLevel.toString())} 할인 {userDiscount.discountRate}% 자동 적용됨
                      </p>
                    ) : null;
                  })()}
                  {locationState.reservationType === 'start-end' && (
                    <p className="text-sm text-[#6b7684] mt-1">
                      {facility.basePrice ? (
                        `(기본 이용가격 ${facility.basePrice.toLocaleString()}원 + 시간당 ${facility.price.toLocaleString()}원 × ${Math.max(0, locationState.endTime - locationState.startTime - 1)}시간)`
                      ) : (
                        `(시간당 ${facility.price.toLocaleString()}원 × ${locationState.endTime - locationState.startTime}시간)`
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* 신청자 정보 섹션 */}
            <div className="p-4 sm:p-6 lg:p-8">
              <h2 className="text-[#1e2c49] text-lg sm:text-xl font-bold font-['Pretendard'] leading-tight mb-4">
                신청자 정보
              </h2>
              <div className="w-full h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6 sm:mb-8"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">이름</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">{locationState.name}</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">이메일</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold break-all">{locationState.email}</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">연락처</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">{locationState.phoneNumber || '정보 없음'}</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-2">소속</p>
                  <p className="text-[#1e2c49] text-lg sm:text-[19px] font-semibold">{locationState.organization || '정보 없음'}</p>
                </div>
              </div>
            </div>

            {/* 감면 사유 섹션 - 일반사용자(level 9)인 경우에만 표시 */}
            {userProfile?.userLevel === 9 && facility?.discounts && facility.discounts.length > 0 && (
              <div className="p-4 sm:p-6 lg:p-8">
                <h2 className="text-[#1e2c49] text-lg sm:text-xl font-bold font-['Pretendard'] leading-tight mb-4">
                  감면 사유
                </h2>
                <div className="w-full h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6 sm:mb-8"></div>
                <div className="space-y-4">
                  <p className="text-[#6b7684] text-base sm:text-lg font-medium mb-4">
                    해당하는 감면 사유를 선택해주세요
                  </p>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="discountType"
                        value=""
                        checked={selectedDiscountType === ''}
                        onChange={(e) => handleDiscountChange(e.target.value)}
                        className="mr-3 w-4 h-4 text-blue-600"
                      />
                      <span className="text-[#1e2c49] text-lg">감면 사유 없음</span>
                    </label>
                    {facility.discounts.map((discount, index) => (
                      <label key={index} className="flex items-center">
                        <input
                          type="radio"
                          name="discountType"
                          value={String(discount.memberType)}
                          checked={selectedDiscountType === String(discount.memberType)}
                          onChange={(e) => handleDiscountChange(e.target.value)}
                          className="mr-3 w-4 h-4 text-blue-600"
                        />
                        <span className="text-[#1e2c49] text-lg">
                          {getUserLevelText(discount.memberType)} ({discount.discountRate}% 할인)
                        </span>
                      </label>
                    ))}
                  </div>
                  {selectedDiscountType && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-sm">
                      <p className="text-blue-700 text-sm">
                        ※ 선택하신 감면 사유는 관리자 승인 후 적용됩니다. 
                        관련 증빙서류가 있으시면 아래 추가 서류에 첨부해주세요.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 추가 서류 섹션 */}
            <div className="p-4 sm:p-6 lg:p-8">
              <h2 className="text-[#1e2c49] text-lg sm:text-xl font-bold font-['Pretendard'] leading-tight mb-4">
                추가 서류
              </h2>
              <div className="w-full h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6 sm:mb-8"></div>
              <div className="space-y-2">
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-[#6b7684]
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-sm file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
                {file && (
                  <p className="text-sm text-[#6b7684]">
                    선택된 파일: {file.name}
                  </p>
                )}
                {userProfile?.userLevel === 9 && selectedDiscountType && (
                  <p className="text-sm text-[#6b7684] mt-2">
                    감면 사유 관련 증빙서류를 첨부해주세요 (재직증명서 등)
                  </p>
                )}
                <p className="text-sm text-[#6b7684] mt-2">
                  첨부파일은 {MAX_RESERVATION_UPLOAD_SIZE_MB}MB 이하만 업로드할 수 있습니다.
                </p>
              </div>
            </div>

            {/* userlevel 4 이하(공사 관계자 제외) 필수 확인 */}
            {userProfile && userProfile.userLevel >= 4 && (
              <>
                {/* 대관규약 섹션 */}
                <div className="p-4 sm:p-6 lg:p-8">
                  <h2 className="text-[#1e2c49] text-lg sm:text-xl font-bold font-['Pretendard'] leading-tight mb-4">
                    대관규약
                  </h2>
                  <div className="w-full h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6 sm:mb-8"></div>
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-sm border border-gray-200 text-[#1e2c49] text-sm leading-6">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="font-semibold mb-2">요약</p>
                          <ul className="list-disc pl-5 space-y-1 text-[#6b7684]">
                            <li>시설물 이용 수칙 및 안전 수칙을 준수해야 합니다.</li>
                            <li>신청 내용과 상이하게 사용될 경우 이용이 제한될 수 있습니다.</li>
                            <li>정확한 일정/인원/목적 등 신청 정보의 사실성을 보장해야 합니다.</li>
                            <li>기타 세부 규정은 운영 기준 및 공지 사항을 따릅니다.</li>
                          </ul>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsRentalTermsModalOpen(true)}
                          className="shrink-0 text-sm text-[#155acb] hover:text-[#155acb]/80 font-['Pretendard'] transition-colors"
                        >
                          전문 보기
                        </button>
                      </div>
                    </div>

                    <label className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={hasAgreedRentalTerms}
                        onChange={(e) => setHasAgreedRentalTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 text-blue-600"
                      />
                      <span className="text-[#1e2c49] text-base">
                        대관규약을 확인하였으며, 이에 동의합니다. (필수)
                      </span>
                    </label>
                  </div>
                </div>

                {/* 대관신청서 섹션 */}
                <div className="p-4 sm:p-6 lg:p-8">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <h2 className="text-[#1e2c49] text-lg sm:text-xl font-bold font-['Pretendard'] leading-tight">
                      대관신청서 첨부 (필수)
                    </h2>
                    <a
                      href={applicationFormTemplateUrl}
                      download={applicationFormTemplateFileName}
                      className="shrink-0 text-sm text-[#155acb] underline hover:text-[#155acb]/80 font-medium"
                    >
                      다운로드
                    </a>
                  </div>
                  <div className="w-full h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6 sm:mb-8"></div>
                  <div className="space-y-2">
                    <input
                      type="file"
                      onChange={handleApplicationFormFileChange}
                      className="block w-full text-sm text-[#6b7684]
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-sm file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                    />
                    {applicationFormFile && (
                      <p className="text-sm text-[#6b7684]">
                        선택된 파일: {applicationFormFile.name}
                      </p>
                    )}
                    <p className="text-sm text-[#6b7684] mt-2">
                      대관신청서를 반드시 작성하여 첨부해주세요. 첨부파일은 {MAX_RESERVATION_UPLOAD_SIZE_MB}MB 이하만 업로드할 수 있습니다.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* 대관규약 전문 모달 */}
          {isRentalTermsModalOpen && (
            <div
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setIsRentalTermsModalOpen(false);
              }}
            >
                <div className="w-full sm:max-w-[640px] bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-h-[calc(90vh-22px)] sm:max-h-[90vh] overflow-hidden flex flex-col">
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="text-[#1e2c49] font-bold font-['Pretendard']">
                    부산항 홍보관 및 복지플러스센터 대관 규약
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsRentalTermsModalOpen(false)}
                    className="text-sm text-[#6b7684] hover:text-[#1e2c49] transition-colors"
                  >
                    닫기
                  </button>
                </div>

                <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1 min-h-0">
                  <div className="text-sm text-[#1e2c49] leading-6 whitespace-pre-line">
                    {RENTAL_TERMS_FULL_TEXT}
                  </div>
                </div>

                <div className="px-4 sm:px-6 py-4 pb-[env(safe-area-inset-bottom)] border-t border-gray-200 flex flex-col sm:flex-row gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setIsRentalTermsModalOpen(false)}
                    className="w-full sm:w-auto h-11 px-4 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    닫기
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setHasAgreedRentalTerms(true);
                      setIsRentalTermsModalOpen(false);
                    }}
                    className="w-full sm:w-auto h-11 px-4 rounded-sm bg-[#155acb] text-white hover:bg-[#155acb]/90"
                  >
                    동의하고 닫기
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 하단 버튼 */}
          <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 pb-[env(safe-area-inset-bottom)]">
            <div className="w-full max-w-[760px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-0 sm:h-24 flex flex-col items-stretch sm:items-end justify-center gap-2 sm:gap-3">
              {submitStatus && (
                <p className="w-full text-center sm:text-right text-sm text-[#6b7684]">
                  {submitStatus}
                </p>
              )}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4">
                <button
                  onClick={() => navigate('/facility')}
                  className="w-full sm:w-44 h-12 sm:h-16 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <div className="text-center text-gray-700 text-base sm:text-lg font-bold font-['Pretendard'] leading-9">
                    취소
                  </div>
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`w-full sm:w-44 h-12 sm:h-16 ${
                    !loading ? 'bg-[#155acb] hover:bg-[#155acb]/90' : 'bg-gray-400'
                  } rounded-sm`}
                >
                  <div className="text-center text-white text-base sm:text-lg font-bold font-['Pretendard'] leading-9">
                    {loading ? '처리 중...' : '예약하기'}
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* 하단 여백 */}
          <div className="h-40 sm:h-32"></div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default FacilityReservationConfirmPage; 
